import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Menu } from '../models/Menu'
import { menuSchema } from '../models/schema'
import {
  createMenu as createMenuAPI,
  deleteMenu as deleteMenuAPI,
  fetchMenus,
  updateMenu as updateMenuAPI,
} from '../services/MenuService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Menu> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'menu',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getMenusSuccess,
  setQuery: setMenuQuery,
  reset: resetMenuState,
  resetQueryAndIds: resetMenuQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Menu }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectMenus = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedMenu = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectMenuById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectMenusByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedMenu =
  (menu: Menu | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Menu>(menu)))

export const getMenus = (venueSlug: string, params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchMenus(venueSlug, params),
    [menuSchema],
    slice.actions
  )

export const createMenu = (venueSlug: string, menu: FormData) =>
  SBAPI.CreateDispatched(
    createMenuAPI(venueSlug, menu),
    menuSchema,
    slice.actions
  )

export const updateMenu = (venueSlug: string, menu: FormData) =>
  SBAPI.UpdateDispatched<Menu>(
    updateMenuAPI(venueSlug, menu),
    menu,
    slice.actions
  )

export const deleteMenu = (venueSlug: string, menu: Menu) =>
  SBAPI.DestroyDispatched<Menu>(
    deleteMenuAPI(venueSlug, menu),
    menu,
    slice.actions
  )
